import * as THREE from 'three'
import type { FeatureResultMesh } from '../mesh/feature'
import { addObject3DToParent } from '../utils/scene-object/addObject3DToParent'
import {
  readModelBodyMetadataStringArray,
  resolveModelBodyIdentity,
} from './model-body-metadata'
import {
  ModelBodySurfaceState,
  type ModelBodySurfaceLifecycleState,
} from './ModelBodySurfaceState'

/**
 * 一个逻辑实体在场景中的显示与拓扑表示。
 *
 * 导入适配器与 Worker 结果回灌都只能通过该结构进入后续特征生命周期。
 */
export interface ModelBodySceneRepresentation {
  /** 当前场景表示的唯一注册键。 */
  representationId: string
  /** 跨特征版本稳定的逻辑实体 ID。 */
  modelBodyId: string
  /** 当前表示对应的 Worker 几何版本 ID。 */
  modelBodyRevisionId: string
  /** 仅用于把外部历史身份归一到当前版本的精确别名。 */
  aliasIds?: readonly string[]
  /** 承载真实可见表面的 Mesh。 */
  displayMesh: THREE.Mesh
  /** source Mesh 脱离物理树时，用于证明该表示仍属于当前场景的拓扑仓库锚点。 */
  sceneScopeAnchor?: THREE.Object3D
  /** 承载 FeaturePoint/Line/Face 的正式拓扑宿主。 */
  topologyOwner?: FeatureResultMesh | null
  /** 拓扑宿主按需创建入口。 */
  resolveTopologyOwner?: () => FeatureResultMesh
  /** 多实体共享显示 Mesh 时，该实体占用的 geometry group 索引。 */
  primitiveIndices?: readonly number[]
  /** 首次注册后是否立即成为活动表示；省略时保持既有的默认活动语义。 */
  initiallyActive?: boolean
}

/**
 * 场景表示对外暴露的统一模型体身份。
 */
export interface ModelBodySceneIdentity {
  /** 跨特征版本稳定的逻辑实体 ID。 */
  modelBodyId: string
  /** 当前场景表示对应的 Worker 几何版本 ID。 */
  modelBodyRevisionId: string
}

/** 内部规范化后的场景表示。 */
interface RegisteredModelBodySceneRepresentation extends ModelBodySceneRepresentation {
  /** 去重后的全部精确引用。 */
  referenceIds: readonly string[]
  /** 当前是否被 Worker 正式状态替换。 */
  isCommittedInactive: boolean
  /** 当前引用该表示的预览键。 */
  previewKeys: Set<string>
  /** 已创建的拓扑宿主。 */
  topologyOwner: FeatureResultMesh | null
  /** 被提交替换时拓扑宿主的原父节点。 */
  topologyOwnerParent: THREE.Object3D | null
  /** 被提交替换时拓扑宿主的原索引。 */
  topologyOwnerChildIndex: number
}

/**
 * Worker 实体状态到 Three.js 场景表示的统一生命周期注册表。
 *
 * 职责：
 * 1. 用同一组 `modelBodyId/modelBodyRevisionId` 规则管理导入体与建模结果体；
 * 2. 统一执行预览表面抑制、提交摘除和 Undo/Redo 恢复；
 * 3. 统一解析 FeatureResultMesh 拓扑宿主，特征功能不得再识别实体来源。
 */
export class ModelBodySceneRegistry {
  /** 按注册键保存场景表示。 */
  private readonly representations = new Map<string, RegisteredModelBodySceneRepresentation>()

  /** 精确引用到注册键的反向索引。 */
  private readonly representationIdsByReferenceId = new Map<string, Set<string>>()

  /** 预览键到目标表示注册键。 */
  private readonly representationIdByPreviewKey = new Map<string, string>()

  /** 预览键到上游实体精确引用；表示短暂重建时仍保留请求。 */
  private readonly sourceReferenceIdByPreviewKey = new Map<string, string>()

  /** 每个显示 Mesh 的共享表面状态控制器。 */
  private readonly surfaceStateByMesh = new Map<THREE.Mesh, ModelBodySurfaceState>()

  /** 显示 Mesh 到其承载实体表示的反向索引。 */
  private readonly representationIdsByDisplayMesh = new Map<THREE.Mesh, Set<string>>()

  /** 拓扑宿主到其唯一实体表示的反向索引。 */
  private readonly representationIdByTopologyOwner = new WeakMap<FeatureResultMesh, string>()

  /**
   * 创建场景实体注册表。
   *
   * @param scene 作用域场景；用于校验解析边界与生命周期归属。
   */
  public constructor(private readonly scene: THREE.Object3D) {}

  /**
   * 注册或更新一个实体场景表示。
   *
   * @param input 来源无关的实体表示描述。
   * @throws 当身份字段为空，或新表示的显示网格不属于当前场景时抛出错误。
   */
  public registerRepresentation(input: ModelBodySceneRepresentation): void {
    const representationId = input.representationId.trim()
    const modelBodyId = input.modelBodyId.trim()
    const modelBodyRevisionId = input.modelBodyRevisionId.trim()
    if (!representationId || !modelBodyId || !modelBodyRevisionId) {
      throw new Error('实体场景表示缺少 representationId/modelBodyId/modelBodyRevisionId')
    }

    const existing = this.representations.get(representationId)
    const belongsToScene = this.isObjectInSceneScope(input.displayMesh)
      || Boolean(input.sceneScopeAnchor && this.isObjectInSceneScope(input.sceneScopeAnchor))
    if (!existing && !belongsToScene) {
      throw new Error(`实体场景表示不属于当前注册表场景: ${representationId}`)
    }
    const previousPreviewKeys = new Set(existing?.previewKeys ?? [])
    const wasCommittedInactive = existing?.isCommittedInactive ?? input.initiallyActive === false
    if (existing && existing.displayMesh !== input.displayMesh) {
      this.unregisterRepresentation(representationId)
    } else if (existing) {
      this.removeReferenceIndexes(existing)
    }

    const referenceIds = this.collectReferenceIds({
      ...input,
      representationId,
      modelBodyId,
      modelBodyRevisionId,
    })
    const representation: RegisteredModelBodySceneRepresentation = {
      ...input,
      representationId,
      modelBodyId,
      modelBodyRevisionId,
      referenceIds,
      aliasIds: [...(input.aliasIds ?? [])],
      topologyOwner: input.topologyOwner ?? existing?.topologyOwner ?? null,
      isCommittedInactive: wasCommittedInactive,
      previewKeys: previousPreviewKeys,
      topologyOwnerParent: existing?.topologyOwnerParent ?? null,
      topologyOwnerChildIndex: existing?.topologyOwnerChildIndex ?? -1,
    }
    if (existing?.topologyOwner && existing.topologyOwner !== representation.topologyOwner) {
      this.representationIdByTopologyOwner.delete(existing.topologyOwner)
    }
    this.representations.set(representationId, representation)
    const displayRepresentationIds = this.representationIdsByDisplayMesh.get(input.displayMesh)
      ?? new Set<string>()
    displayRepresentationIds.add(representationId)
    this.representationIdsByDisplayMesh.set(input.displayMesh, displayRepresentationIds)
    if (representation.topologyOwner) {
      this.representationIdByTopologyOwner.set(representation.topologyOwner, representationId)
    }
    this.addReferenceIndexes(representation)
    previousPreviewKeys.forEach((previewKey) => {
      this.representationIdByPreviewKey.set(previewKey, representationId)
    })

    const surfaceState = this.surfaceStateByMesh.get(input.displayMesh)
      ?? new ModelBodySurfaceState(input.displayMesh)
    this.surfaceStateByMesh.set(input.displayMesh, surfaceState)
    surfaceState.registerPartition(representationId, input.primitiveIndices)
    this.bindPendingPreviewRequests(representation)
    this.applyRepresentationState(representation)
  }

  /**
   * 注销场景表示，并恢复由注册表施加的临时状态。
   *
   * @param representationId 注册键。
   */
  public unregisterRepresentation(representationId: string): void {
    const representation = this.representations.get(representationId)
    if (!representation) {
      return
    }
    representation.previewKeys.forEach((previewKey) => {
      this.representationIdByPreviewKey.delete(previewKey)
    })
    representation.previewKeys.clear()
    representation.topologyOwner?.setTopologyDisplayEdgesSuppressed(false)
    if (representation.topologyOwner) {
      this.representationIdByTopologyOwner.delete(representation.topologyOwner)
    }
    this.restoreTopologyOwner(representation)
    this.removeReferenceIndexes(representation)
    this.representations.delete(representationId)

    const displayRepresentationIds = this.representationIdsByDisplayMesh.get(representation.displayMesh)
    displayRepresentationIds?.delete(representationId)
    if (displayRepresentationIds?.size === 0) {
      this.representationIdsByDisplayMesh.delete(representation.displayMesh)
    }

    const surfaceState = this.surfaceStateByMesh.get(representation.displayMesh)
    surfaceState?.unregisterPartition(representationId)
    const stillUsesMesh = [...this.representations.values()]
      .some((candidate) => candidate.displayMesh === representation.displayMesh)
    if (!stillUsesMesh && surfaceState) {
      surfaceState.dispose()
      this.surfaceStateByMesh.delete(representation.displayMesh)
    }
  }

  /**
   * 将目标实体切入预览替换态。
   *
   * @param previewKey 当前预览实例键。
   * @param sourceReferenceId 上游几何版本 ID 或已注册精确别名。
   */
  public beginPreviewReplacement(previewKey: string, sourceReferenceId: string): void {
    this.endPreviewReplacement(previewKey)
    const normalizedSourceReferenceId = sourceReferenceId.trim()
    if (!normalizedSourceReferenceId) {
      return
    }
    this.sourceReferenceIdByPreviewKey.set(previewKey, normalizedSourceReferenceId)
    const representation = this.resolveRepresentation(normalizedSourceReferenceId)
    if (!representation || representation.isCommittedInactive) {
      return
    }
    representation.previewKeys.add(previewKey)
    this.representationIdByPreviewKey.set(previewKey, representation.representationId)
    this.applyRepresentationState(representation)
  }

  /**
   * 结束指定预览替换并恢复正式表面状态。
   *
   * @param previewKey 当前预览实例键。
   */
  public endPreviewReplacement(previewKey: string): void {
    this.sourceReferenceIdByPreviewKey.delete(previewKey)
    const representationId = this.representationIdByPreviewKey.get(previewKey)
    if (!representationId) {
      return
    }
    this.representationIdByPreviewKey.delete(previewKey)
    const representation = this.representations.get(representationId)
    if (!representation) {
      return
    }
    representation.previewKeys.delete(previewKey)
    this.applyRepresentationState(representation)
  }

  /**
   * 根据 Worker 当前权威对象集合刷新全部实体表示。
   *
   * @param objects Worker 当前活动对象。
   */
  public applyWorkerState(objects: readonly GeometryObject[]): void {
    this.applyWorkerStateWithReplayPolicy(objects, null)
  }

  /**
   * 按 Feature replay 结果刷新场景表示，并保留未被活动结果消费的导入基线。
   *
   * 与 `applyWorkerState()` 的区别仅在导入基线：普通同步把 Worker objects 当作完整可见集合；
   * Feature replay 则把它解释为活动 Feature 产出集合，导入基线即使未出现在集合中也仍是历史起点。
   *
   * @param objects Worker 当前活动 Feature 结果及可能显式返回的基线对象。
   * @param baselineRevisionIds 项目打开时从 Worker 冻结的纯导入基线修订 ID。
   */
  public applyFeatureReplayState(
    objects: readonly GeometryObject[],
    baselineRevisionIds: ReadonlySet<string>,
  ): void {
    this.applyWorkerStateWithReplayPolicy(objects, baselineRevisionIds)
  }

  /**
   * 按注册表示 ID 暂停一组场景表示，但保留其身份、拓扑与恢复能力。
   *
   * 失败降级必须按表示角色精确停用，不能按 revision 批量处理：同一原始导入 revision
   * 可能同时代表 replay 补建候选和离线最终快照中仍合法可见的未修改实体。
   * 重新激活必须回到 `applyFeatureReplayState()`，继续服从 Worker 当前状态。
   *
   * @param representationIds 需要暂停显示的注册表示 ID。
   */
  public deactivateRepresentations(representationIds: ReadonlySet<string>): void {
    this.representations.forEach((representation) => {
      if (!representationIds.has(representation.representationId)) {
        return
      }
      representation.isCommittedInactive = true
      this.applyRepresentationState(representation)
    })
  }

  /**
   * 根据 Worker 对象集合和 replay 基线策略刷新全部场景表示。
   *
   * @param objects Worker 当前活动对象。
   * @param baselineRevisionIds 允许作为当前 replay 初始状态保留的修订 ID；null 表示严格 Worker 全集语义。
   */
  private applyWorkerStateWithReplayPolicy(
    objects: readonly GeometryObject[],
    baselineRevisionIds: ReadonlySet<string> | null,
  ): void {
    const activeRevisionIds = new Set<string>()
    const activeRevisionIdsByModelBodyId = new Map<string, Set<string>>()
    const consumedRevisionIds = new Set<string>()
    objects.forEach((object) => {
      const identity = resolveModelBodyIdentity(object)
      if (identity) {
        activeRevisionIds.add(identity.modelBodyRevisionId)
        const revisions = activeRevisionIdsByModelBodyId.get(identity.modelBodyId)
          ?? new Set<string>()
        revisions.add(identity.modelBodyRevisionId)
        activeRevisionIdsByModelBodyId.set(identity.modelBodyId, revisions)
      }
      readModelBodyMetadataStringArray(object.metadata?.consumedModelBodyRevisionIds)
        .forEach((revisionId) => consumedRevisionIds.add(revisionId))
    })

    this.representations.forEach((representation) => {
      /**
       * 完整 Worker 摘要是正式实体修订的权威集合。历史结果即使没有被当前对象再次显式声明为 consumed，
       * 只要不在该集合中就必须失活；否则编辑回放到前驱状态时，旧最终修订会继续阻止前驱网格恢复。
       */
      const hasExactActiveRevision = activeRevisionIds.has(representation.modelBodyRevisionId)
      const hasReplacingLogicalRevision = [
        ...(activeRevisionIdsByModelBodyId.get(representation.modelBodyId) ?? []),
      ].some((revisionId) => revisionId !== representation.modelBodyRevisionId)
      const isConsumedByActiveResult = consumedRevisionIds.has(representation.modelBodyRevisionId)
      const isAvailableReplayBaseline = baselineRevisionIds !== null
        && baselineRevisionIds.has(representation.modelBodyRevisionId)
        && !hasReplacingLogicalRevision
        && !isConsumedByActiveResult

      representation.isCommittedInactive = !hasExactActiveRevision && !isAvailableReplayBaseline
      this.applyRepresentationState(representation)
    })
  }

  /**
   * 增量应用单个 Worker 结果体的替换声明。
   *
   * 该入口只失活该结果明确消费的旧版本，不会恢复其它历史实体；完整 Undo/Redo 恢复必须使用
   * `applyWorkerState()` 的权威状态快照。
   *
   * @param object 新增或替换的 Worker 结果体。
   */
  public applyWorkerObject(object: GeometryObject): void {
    const identity = resolveModelBodyIdentity(object)
    const consumedRevisionIds = new Set(
      readModelBodyMetadataStringArray(object.metadata?.consumedModelBodyRevisionIds),
    )
    this.representations.forEach((representation) => {
      if (identity?.modelBodyRevisionId === representation.modelBodyRevisionId) {
        representation.isCommittedInactive = false
      } else if (consumedRevisionIds.has(representation.modelBodyRevisionId)) {
        representation.isCommittedInactive = true
      } else {
        return
      }
      this.applyRepresentationState(representation)
    })
  }

  /**
   * 按模型体修订 ID 或导入边界登记的精确别名解析统一场景身份。
   *
   * 设计说明：
   * 1. 该方法不要求表示处于活动态，设计树需要用它识别已经被提交特征替换的旧修订；
   * 2. 装配 occurrence、线性阵列实例可共享同一 Worker 几何修订，但拥有各自独立的显示 Mesh；
   * 3. 身份查询只要求命中的表示归属于同一模型体身份，不得把多个等价场景表示误判为身份歧义。
   *
   * @param referenceId 几何版本、逻辑实体或边界别名。
   * @returns 唯一模型体身份；未登记时返回 null，命中不同身份时抛出错误。
   * @throws 当同一引用命中多个不同的模型体身份时抛出错误。
   */
  public resolveSceneIdentity(referenceId: string): ModelBodySceneIdentity | null {
    const matches = this.collectIdentityResolutionMatches(referenceId)
    if (matches.length === 0) {
      return null
    }

    /**
     * 稳定别名可能同时登记历史修订与当前修订。只要存在活动表示，就以活动集合为权威；
     * 全部失活时才允许设计树继续识别历史身份。
     */
    const activeMatches = matches.filter((representation) => !representation.isCommittedInactive)
    const identityMatches = activeMatches.length > 0 ? activeMatches : matches
    const firstMatch = identityMatches[0]
    if (!firstMatch) {
      return null
    }
    const hasConflictingIdentity = identityMatches.some((representation) => (
      representation.modelBodyId !== firstMatch.modelBodyId
      || representation.modelBodyRevisionId !== firstMatch.modelBodyRevisionId
    ))
    if (hasConflictingIdentity) {
      const ambiguityKind = activeMatches.length > 0 ? '活动身份' : '身份'
      throw new Error(`实体场景${ambiguityKind}存在歧义: ${referenceId.trim()}`)
    }

    return {
      modelBodyId: firstMatch.modelBodyId,
      modelBodyRevisionId: firstMatch.modelBodyRevisionId,
    }
  }

  /**
   * 判断指定修订是否已经由活动场景表示承载。
   *
   * Viewer 使用该查询决定是否需要从 Worker 重新网格化。导入 GLB 表面与手工特征结果体
   * 都通过同一注册表回答，不允许再按来源类型分流。
   *
   * @param referenceId 当前 Worker 修订 ID 或其它精确引用。
   * @returns 存在且未被提交替换时返回 true。
   */
  public hasActiveRepresentation(referenceId: string): boolean {
    return this.collectIdentityResolutionMatches(referenceId)
      .some((representation) => !representation.isCommittedInactive)
  }

  /**
   * 判断指定 Worker 修订是否已经拥有任意场景表示。
   *
   * 与 `hasActiveRepresentation()` 不同，本查询只接受精确 revision ID，且不要求表示当前可见。
   * 项目打开阶段用它判断是否需要从 Worker 原始 BRep 物化缺失的 replay baseline，不能按
   * `modelBodyId` 命中最终 Feature 快照后误判“原始修订已经有显示所有者”。
   *
   * @param modelBodyRevisionId Worker 几何修订 ID。
   * @returns 是否存在精确修订表示。
   */
  public hasRepresentationRevision(modelBodyRevisionId: string): boolean {
    const normalizedRevisionId = modelBodyRevisionId.trim()
    return normalizedRevisionId.length > 0 && [...this.representations.values()]
      .some((representation) => representation.modelBodyRevisionId === normalizedRevisionId)
  }

  /**
   * 按 Worker 权威实体引用解析当前唯一的可见表面。
   *
   * 设计说明：
   * 1. 设计树和特征定义只持久化模型体修订 ID，不持有易失的 Three.js UUID；
   * 2. 导入 GLB 表面与特征结果体都通过同一注册表声明当前显示所有权；
   * 3. 已被特征消费的旧表示不会返回，避免选择链重新命中已交接或已失活的表面。
   *
   * @param referenceId Worker 几何修订 ID、逻辑实体 ID 或已登记精确别名。
   * @returns 当前活动表示的显示 Mesh；未登记、已失活或引用不唯一时返回 null。
   */
  public resolveActiveDisplayMesh(referenceId: string): THREE.Mesh | null {
    const normalizedReferenceId = referenceId.trim()
    if (!normalizedReferenceId) {
      return null
    }

    /** 几何修订 ID 优先，确保逻辑实体存在历史版本时只命中当前精确版本。 */
    const exactRevisionMatches = [...this.representations.values()].filter((representation) => (
      !representation.isCommittedInactive
      && representation.modelBodyRevisionId === normalizedReferenceId
    ))
    if (exactRevisionMatches.length === 1) {
      return exactRevisionMatches[0]?.displayMesh ?? null
    }
    if (exactRevisionMatches.length > 1) {
      return null
    }

    /**
     * 逻辑实体或边界别名可能在装配体中对应多个活动实例；选择入口没有实例作用域时
     * 必须拒绝猜测，只有唯一活动表示才可作为当前显示所有者。
     */
    const representationIds = this.representationIdsByReferenceId.get(normalizedReferenceId)
    const activeMatches = [...(representationIds ?? [])]
      .map((representationId) => this.representations.get(representationId))
      .filter((representation): representation is RegisteredModelBodySceneRepresentation => (
        Boolean(representation) && representation?.isCommittedInactive === false
      ))
    return activeMatches.length === 1 ? activeMatches[0]?.displayMesh ?? null : null
  }

  /**
   * 按实体任意精确引用解析活动拓扑宿主。
   *
   * @param referenceId 几何版本、逻辑实体或边界适配别名。
   * @returns FeatureResultMesh；目标已被替换或未注册时返回 null。
   */
  public resolveTopologyOwner(referenceId: string): FeatureResultMesh | null {
    const representation = this.resolveRepresentation(referenceId)
    if (!representation || representation.isCommittedInactive) {
      return null
    }
    const topologyOwner = this.resolveRegisteredTopologyOwner(representation)
    topologyOwner?.setTopologyDisplayEdgesSuppressed(
      representation.previewKeys.size > 0,
    )
    return topologyOwner
  }

  /**
   * 判断场景对象是否属于当前被特征预览替换的源实体。
   *
   * 设计说明：
   * 1. 该判定只表达统一实体生命周期，不区分导入体、手工结果体或具体特征类型；
   * 2. 拓扑代理优先按唯一宿主精确归属，避免共享显示 Mesh 时把实体 B 误判成实体 A；
   * 3. 对共享显示 Mesh，只有当前可命中的全部活动分区都处于预览替换态时才返回 true，
   *    因为部分替换时表面控制器已经只保留其它活动实体的 geometry group。
   *
   * @param object 当前拾取命中的场景对象。
   * @returns 对象属于预览替换源时返回 true。
   */
  public isPreviewReplacementSourceObject(object: THREE.Object3D): boolean {
    let current: THREE.Object3D | null = object
    while (current) {
      if (current instanceof THREE.Mesh) {
        const topologyRepresentationId = this.representationIdByTopologyOwner.get(
          current as FeatureResultMesh,
        )
        const topologyRepresentation = topologyRepresentationId
          ? this.representations.get(topologyRepresentationId)
          : null
        if (topologyRepresentation) {
          return !topologyRepresentation.isCommittedInactive
            && topologyRepresentation.previewKeys.size > 0
        }

        const displayRepresentationIds = this.representationIdsByDisplayMesh.get(current)
        if (displayRepresentationIds && displayRepresentationIds.size > 0) {
          const activeRepresentations = [...displayRepresentationIds]
            .map((representationId) => this.representations.get(representationId))
            .filter((representation): representation is RegisteredModelBodySceneRepresentation => (
              Boolean(representation) && representation?.isCommittedInactive === false
            ))
          return activeRepresentations.length > 0
            && activeRepresentations.every((representation) => representation.previewKeys.size > 0)
        }
      }
      current = current.parent
    }
    return false
  }

  /** 恢复全部场景状态并释放注册表自身资源。 */
  public dispose(): void {
    this.representations.forEach((representation) => {
      representation.topologyOwner?.setTopologyDisplayEdgesSuppressed(false)
      this.restoreTopologyOwner(representation)
    })
    this.surfaceStateByMesh.forEach((surfaceState) => surfaceState.dispose())
    this.surfaceStateByMesh.clear()
    this.representations.clear()
    this.representationIdsByReferenceId.clear()
    this.representationIdsByDisplayMesh.clear()
    this.representationIdByPreviewKey.clear()
    this.sourceReferenceIdByPreviewKey.clear()
  }

  /**
   * 收集身份查询与活动存在性查询可使用的场景表示。
   *
   * 设计说明：
   * 1. 几何修订 ID 优先于逻辑实体和边界别名，避免旧修订查询被当前别名重定向；
   * 2. 返回多个结果是装配 occurrence 共享 Worker 几何时的合法状态；
   * 3. 调用方必须按自身语义判断“身份等价”“至少一个活动”或“物理表示唯一”，禁止混用。
   *
   * @param referenceId 几何修订 ID、逻辑实体 ID 或边界别名。
   * @returns 按精确修订优先级收集的场景表示。
   */
  private collectIdentityResolutionMatches(
    referenceId: string,
  ): RegisteredModelBodySceneRepresentation[] {
    const normalizedReferenceId = referenceId.trim()
    if (!normalizedReferenceId) {
      return []
    }

    const exactRevisionMatches = [...this.representations.values()]
      .filter((representation) => representation.modelBodyRevisionId === normalizedReferenceId)
    if (exactRevisionMatches.length > 0) {
      return exactRevisionMatches
    }

    const representationIds = this.representationIdsByReferenceId.get(normalizedReferenceId)
    return [...(representationIds ?? [])]
      .map((representationId) => this.representations.get(representationId))
      .filter((representation): representation is RegisteredModelBodySceneRepresentation => (
        Boolean(representation)
      ))
  }

  /**
   * 按精确引用解析唯一场景表示。
   *
   * @param referenceId 查询引用。
   * @returns 唯一表示；未命中返回 null。
   */
  private resolveRepresentation(
    referenceId: string,
  ): RegisteredModelBodySceneRepresentation | null {
    const normalizedReferenceId = referenceId.trim()
    if (!normalizedReferenceId) {
      return null
    }

    /** 几何版本 ID 优先，避免逻辑实体存在历史表示时产生歧义。 */
    const exactRevisionMatches = [...this.representations.values()].filter((representation) => (
      representation.modelBodyRevisionId === normalizedReferenceId
    ))
    if (exactRevisionMatches.length === 1) {
      return exactRevisionMatches[0] ?? null
    }

    const representationIds = this.representationIdsByReferenceId.get(normalizedReferenceId)
    const matches = [...(representationIds ?? [])]
      .map((representationId) => this.representations.get(representationId))
      .filter((representation): representation is RegisteredModelBodySceneRepresentation => Boolean(representation))
    const activeMatches = matches.filter((representation) => !representation.isCommittedInactive)
    if (activeMatches.length === 1) {
      return activeMatches[0] ?? null
    }
    if (activeMatches.length > 1) {
      throw new Error(`实体场景活动引用存在歧义: ${normalizedReferenceId}`)
    }
    if (matches.length > 1) {
      throw new Error(`实体场景引用存在歧义: ${normalizedReferenceId}`)
    }
    return matches[0] ?? null
  }

  /**
   * 判断对象是否仍位于当前注册表负责的场景子树。
   *
   * @param object 待校验对象。
   * @returns 对象自身或任一祖先是当前场景时返回 true。
   */
  private isObjectInSceneScope(object: THREE.Object3D): boolean {
    let current: THREE.Object3D | null = object
    while (current) {
      if (current === this.scene) {
        return true
      }
      current = current.parent
    }
    return false
  }

  /**
   * 应用单个表示的有效生命周期状态。
   *
   * @param representation 目标场景表示。
   */
  private applyRepresentationState(
    representation: RegisteredModelBodySceneRepresentation,
  ): void {
    const state: ModelBodySurfaceLifecycleState = representation.isCommittedInactive
      ? 'inactive'
      : representation.previewKeys.size > 0
        ? 'preview'
        : 'active'
    this.surfaceStateByMesh.get(representation.displayMesh)
      ?.setPartitionState(representation.representationId, state)

    /**
     * 预览期间必须保留正式拓扑宿主用于后续点线面拾取，但其浏览态默认黑边不能继续显示。
     * 导入实体的 host 可能尚未创建，因此只在真正进入预览时按需解析一次。
     */
    const topologyOwner = state === 'preview'
      ? this.resolveRegisteredTopologyOwner(representation)
      : representation.topologyOwner
    topologyOwner?.setTopologyDisplayEdgesSuppressed(state === 'preview')

    if (state === 'inactive') {
      this.detachTopologyOwner(representation)
    } else {
      this.restoreTopologyOwner(representation)
    }
  }

  /**
   * 读取或按需创建已注册表示的正式拓扑宿主。
   *
   * @param representation 目标场景表示。
   * @returns FeatureResultMesh 拓扑宿主；表示不提供拓扑能力时返回 null。
   */
  private resolveRegisteredTopologyOwner(
    representation: RegisteredModelBodySceneRepresentation,
  ): FeatureResultMesh | null {
    if (!representation.topologyOwner && representation.resolveTopologyOwner) {
      representation.topologyOwner = representation.resolveTopologyOwner()
      this.representationIdByTopologyOwner.set(
        representation.topologyOwner,
        representation.representationId,
      )
    }
    return representation.topologyOwner
  }

  /**
   * 提交替换后摘除独立拓扑宿主；预览态不调用该入口。
   *
   * @param representation 目标场景表示。
   */
  private detachTopologyOwner(representation: RegisteredModelBodySceneRepresentation): void {
    const topologyOwner = representation.topologyOwner
    if (!topologyOwner || topologyOwner === representation.displayMesh || !topologyOwner.parent) {
      return
    }
    if (!representation.topologyOwnerParent) {
      representation.topologyOwnerParent = topologyOwner.parent
      representation.topologyOwnerChildIndex = topologyOwner.parent.children.indexOf(topologyOwner)
    }
    topologyOwner.removeFromParent()
  }

  /**
   * Undo/Redo 或预览结束时恢复独立拓扑宿主。
   *
   * @param representation 目标场景表示。
   */
  private restoreTopologyOwner(representation: RegisteredModelBodySceneRepresentation): void {
    const topologyOwner = representation.topologyOwner
    const parent = representation.topologyOwnerParent
    if (!topologyOwner || !parent || topologyOwner.parent === parent) {
      return
    }
    addObject3DToParent(
      topologyOwner,
      parent,
      representation.topologyOwnerChildIndex >= 0
        ? representation.topologyOwnerChildIndex
        : undefined,
    )
  }

  /**
   * 收集场景表示的全部精确引用。
   *
   * @param representation 场景表示。
   * @returns 去重引用列表。
   */
  private collectReferenceIds(representation: ModelBodySceneRepresentation): string[] {
    const referenceIds = new Set<string>([
      representation.representationId,
      representation.modelBodyId,
      representation.modelBodyRevisionId,
    ])
    representation.aliasIds?.forEach((aliasId) => {
      const normalizedAliasId = aliasId.trim()
      if (normalizedAliasId) {
        referenceIds.add(normalizedAliasId)
      }
    })
    return [...referenceIds]
  }

  /** 为场景表示写入反向引用索引。 */
  private addReferenceIndexes(representation: RegisteredModelBodySceneRepresentation): void {
    representation.referenceIds.forEach((referenceId) => {
      const representationIds = this.representationIdsByReferenceId.get(referenceId) ?? new Set<string>()
      representationIds.add(representation.representationId)
      this.representationIdsByReferenceId.set(referenceId, representationIds)
    })
  }

  /** 从反向引用索引移除场景表示。 */
  private removeReferenceIndexes(representation: RegisteredModelBodySceneRepresentation): void {
    representation.referenceIds.forEach((referenceId) => {
      const representationIds = this.representationIdsByReferenceId.get(referenceId)
      representationIds?.delete(representation.representationId)
      if (representationIds?.size === 0) {
        this.representationIdsByReferenceId.delete(referenceId)
      }
    })
  }

  /**
   * 新表示注册后重新绑定重建期间保留的预览请求。
   *
   * @param representation 新注册的场景表示。
   */
  private bindPendingPreviewRequests(
    representation: RegisteredModelBodySceneRepresentation,
  ): void {
    this.sourceReferenceIdByPreviewKey.forEach((sourceReferenceId, previewKey) => {
      if (this.representationIdByPreviewKey.has(previewKey)) {
        return
      }
      const resolvedRepresentation = this.resolveRepresentation(sourceReferenceId)
      if (resolvedRepresentation !== representation || representation.isCommittedInactive) {
        return
      }
      representation.previewKeys.add(previewKey)
      this.representationIdByPreviewKey.set(previewKey, representation.representationId)
    })
  }
}
